import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getEmergencyCategoryForm,
  updateEmergencyCategoryForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  MapPinIcon,
  NurseIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { buildPhysicians } from "../../../patientForm.shared";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import { resolveFormActorRole } from "../disciplineFormAccess";
import {
  useDedicatedFormLoadEffect,
  useSessionUser,
} from "../useDedicatedFormLoadEffect";
import {
  CALL_911_ITEMS,
  EMERGENCY_PROTOCOL_NOTICE,
  PREPAREDNESS_KIT_ITEMS,
  REPORT_TO_CLINICIAN_ITEMS,
  TRIAGE_CATEGORIES,
} from "./emergencyCategoryContent";

/** A plan date may be scheduled ahead, so the picker must allow future dates. */
const PLAN_DATE_MAX = "2999-12-31";

/** The field only accepts a full 10-digit NPI, so partial values are ignored. */
const normalizeNpi = (npi) => {
  const digits = String(npi || "").replace(/\D/g, "");
  return digits.length === 10 ? digits : "";
};

const ReferenceList = ({ title, items, tone }) => (
  <div className={`rounded-md border overflow-hidden ${tone.border} h-full`}>
    <div
      className={`flex items-center gap-1.5 px-3 py-1.5 border-b ${tone.head} ${tone.border}`}
    >
      <tone.Icon size={12} className={tone.iconClass} />
      <h4
        className={`text-[10px] font-semibold uppercase tracking-wide ${tone.titleClass}`}
      >
        {title}
      </h4>
    </div>
    <ol className="divide-y divide-slate-100">
      {items.map((item, index) => (
        <li key={item} className="flex gap-2 px-3 py-1.5">
          <span
            className={`text-[10px] font-bold tabular-nums shrink-0 pt-px ${tone.numberClass}`}
          >
            {String(index + 1).padStart(2, "0")}.
          </span>
          <span className="text-[11px] leading-snug text-slate-600">
            {item}
          </span>
        </li>
      ))}
    </ol>
  </div>
);

ReferenceList.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string),
  tone: PropTypes.object,
};

const EmergencyCategoryForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useSessionUser();
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  // The server prefills the physician only when it first creates the record,
  // so forms opened from before a physician was added fall back to this.
  const firstPhysician = useMemo(
    () => buildPhysicians(patient).find((p) => p.physicianName?.trim()) || null,
    [patient],
  );

  const hydrate = useCallback((data) => {
    setForm(data);
    setValues({
      planDate: data.planDate || "",
      employeeName: data.employeeName || "",
      patientPhone: data.patientPhone || "",
      patientHomeNumber: data.patientHomeNumber || "",
      addressLine: data.addressLine || "",
      city: data.city || "",
      state: data.state || "",
      zipCode: data.zipCode || "",
      emergencyContactName: data.emergencyContactName || "",
      emergencyContactPhone: data.emergencyContactPhone || "",
      emergencyContactRelationship: data.emergencyContactRelationship || "",
      nearestHospitalFacility: data.nearestHospitalFacility || "",
      hospitalMainPhone: data.hospitalMainPhone || "",
      hospitalAddress: data.hospitalAddress || "",
      primaryPhysicianName:
        data.primaryPhysicianName || firstPhysician?.physicianName || "",
      physicianPhone:
        data.physicianPhone || firstPhysician?.physicianPhone || "",
      physicianNpiNumber:
        data.physicianNpiNumber || normalizeNpi(firstPhysician?.npiNumber),
      kitBatteryRadio: Boolean(data.kitBatteryRadio),
      kitThreeDayFood: Boolean(data.kitThreeDayFood),
      kitBatteriesFlashlight: Boolean(data.kitBatteriesFlashlight),
      kitThreeDayWater: Boolean(data.kitThreeDayWater),
      kitFirstAidSupplies: Boolean(data.kitFirstAidSupplies),
      kitMedicationRecords: Boolean(data.kitMedicationRecords),
      extraSupplyNotes: data.extraSupplyNotes || "",
      triageCategory: data.triageCategory || "",
      triageNotes: data.triageNotes || "",
      patientSignatureName: data.patientSignatureName || patientName,
      clinicianSignatureName: data.clinicianSignatureName || "",
      reviewComments: data.reviewComments || "",
    });
    resetSignatures();
  }, [firstPhysician, patientName, resetSignatures]);

  useDedicatedFormLoadEffect(patientId, formId, isDraft, async (isCancelled) => {
    setLoading(true);

    if (isDraft) {
      hydrate({
        formName:
          clinicalForm.formName ||
          draft?.formName ||
          "Emergency Category & Preparedness Plan",
        formType: clinicalForm.formType || draft?.formType,
        formSection: clinicalForm.formSection || draft?.formSection,
        status: "pending",
        patientName,
        ...draftCaregiverIdentity(currentUser, role),
        permissions: buildClinicalFormPermissions("pending", role),
      });
      if (!isCancelled()) {
        setLoading(false);
        onReady?.();
      }
      return;
    }

    try {
      const res = await getEmergencyCategoryForm(patientId, formId);
      if (isCancelled()) return;
      hydrate(res.result?.form || res.form);
    } catch (err) {
      if (isCancelled()) return;
      toast.error(err.message || "Failed to load form");
      onBack();
    } finally {
      if (isCancelled()) return;
      setLoading(false);
      onReady?.();
    }
  });

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  // Printing renders the identical sheet, only frozen: no editing, no
  // signing, no action buttons.
  const { permissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId: form?.filledBy || clinicalForm?.filledBy || (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });
  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const showPatientSignature = Boolean(permissions.canViewPatientSignature);
  /** Only the assigned clinician captures signatures; Admin/OA just read them. */
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const payload = { ...values, flag };
    payload.planDate = values.planDate
      ? formatDateForInput(values.planDate)
      : null;
    payload.triageCategory = values.triageCategory || null;

    // Admin/OA never sign, so signature keys are only sent by the clinician.
    return canSign
      ? { ...payload, ...signatures.buildSignaturePayload() }
      : payload;
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateEmergencyCategoryForm,
        filledByUserId,
        scheduleDate: values.planDate
          ? formatDateForInput(values.planDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;

  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {/* Page chrome — stays outside the printed sheet */}
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      {/* The form itself — a single white sheet with margins on both sides */}
      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Emergency Category &amp; Preparedness
          </h1>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        {/* Shared identity strip: Patient / MRN / DOB / Caregiver / Date */}
        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth || form.patientDateOfBirth,
          )}
          dateLabel="Date"
          dateValue={displayDateUS(values.planDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="planDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.planDate) || ""}
                  onChange={(event) => setValue("planDate", event.target.value)}
                  max={PLAN_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
        />

        <FormMetaRow form={form} printMode={printMode} />

        {!printMode && (
          <LockedReasonBanner reason={permissions.lockedReason} />
        )}

        {/* Protocol notice */}
        <section className="flex items-start gap-2.5 rounded-md border border-red-200 bg-red-50/70 px-3 py-2.5">
          <span className="flex items-center justify-center h-7 w-7 shrink-0 rounded bg-red-500 text-white">
            <AlertCircleIcon size={15} />
          </span>
          <div className="min-w-0">
            <h3 className="text-[10px] font-bold uppercase tracking-wide text-red-700">
              {EMERGENCY_PROTOCOL_NOTICE.title}
            </h3>
            <p className="mt-0.5 text-[11px] leading-snug text-red-900/80">
              {EMERGENCY_PROTOCOL_NOTICE.body}
            </p>
          </div>
        </section>

        <Section icon={PhoneIcon} title="Emergency Contact Information">
          <SubHeading>Patient contact details</SubHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-3 gap-y-2">
            <Field label="Patient Phone" span="lg:col-span-3">
              <input
                type="tel"
                className={fieldClass}
                value={values.patientPhone}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("patientPhone", e.target.value)}
              />
            </Field>
            <Field label="Patient Home Number" span="lg:col-span-3">
              <input
                type="tel"
                className={fieldClass}
                value={values.patientHomeNumber}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("patientHomeNumber", e.target.value)}
              />
            </Field>
            <Field label="Street Address" span="lg:col-span-6">
              <input
                type="text"
                className={fieldClass}
                value={values.addressLine}
                disabled={!canEdit}
                placeholder="Street address"
                onChange={(e) => setValue("addressLine", e.target.value)}
              />
            </Field>
            <Field label="City" span="lg:col-span-3">
              <input
                type="text"
                className={fieldClass}
                value={values.city}
                disabled={!canEdit}
                onChange={(e) => setValue("city", e.target.value)}
              />
            </Field>
            <Field label="State" span="lg:col-span-1">
              <input
                type="text"
                className={fieldClass}
                value={values.state}
                disabled={!canEdit}
                onChange={(e) => setValue("state", e.target.value)}
              />
            </Field>
            <Field label="Zip Code" span="lg:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.zipCode}
                disabled={!canEdit}
                onChange={(e) => setValue("zipCode", e.target.value)}
              />
            </Field>
          </div>

          <div className="mt-3 pt-3 border-t border-slate-100">
            <SubHeading>Emergency contact person</SubHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-3 gap-y-2">
              <Field label="Emergency Contact Person Name" span="lg:col-span-3">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.emergencyContactName}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("emergencyContactName", e.target.value)
                  }
                />
              </Field>
              <Field label="Contact Phone" span="lg:col-span-2">
                <input
                  type="tel"
                  className={fieldClass}
                  value={values.emergencyContactPhone}
                  disabled={!canEdit}
                  placeholder="(000) 000-0000"
                  onChange={(e) =>
                    setValue("emergencyContactPhone", e.target.value)
                  }
                />
              </Field>
              <Field label="Relationship" span="lg:col-span-1">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.emergencyContactRelationship}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("emergencyContactRelationship", e.target.value)
                  }
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section icon={MapPinIcon} title="Hospital Facility Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-3 gap-y-2">
            <Field label="Nearest Hospital Facility" span="lg:col-span-4">
              <input
                type="text"
                className={fieldClass}
                value={values.nearestHospitalFacility}
                disabled={!canEdit}
                placeholder="Enter facility name"
                onChange={(e) =>
                  setValue("nearestHospitalFacility", e.target.value)
                }
              />
            </Field>
            <Field label="Hospital Main Phone" span="lg:col-span-2">
              <input
                type="tel"
                className={fieldClass}
                value={values.hospitalMainPhone}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("hospitalMainPhone", e.target.value)}
              />
            </Field>
            <Field label="Hospital Address" span="lg:col-span-6">
              <input
                type="text"
                className={fieldClass}
                value={values.hospitalAddress}
                disabled={!canEdit}
                placeholder="Street, city, state, zip"
                onChange={(e) => setValue("hospitalAddress", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={NurseIcon} title="Primary Physician Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-3 gap-y-2">
            <Field label="Primary Physician Name" span="lg:col-span-4">
              <input
                type="text"
                className={fieldClass}
                value={values.primaryPhysicianName}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("primaryPhysicianName", e.target.value)
                }
              />
            </Field>
            <Field label="Physician Phone" span="lg:col-span-2">
              <input
                type="tel"
                className={fieldClass}
                value={values.physicianPhone}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("physicianPhone", e.target.value)}
              />
            </Field>
            <Field label="Physician NPI Number" span="lg:col-span-3">
              <input
                type="text"
                inputMode="numeric"
                maxLength={10}
                className={fieldClass}
                value={values.physicianNpiNumber}
                disabled={!canEdit}
                placeholder="Enter 10-digit NPI"
                onChange={(e) =>
                  setValue(
                    "physicianNpiNumber",
                    e.target.value.replace(/\D/g, "").slice(0, 10),
                  )
                }
              />
            </Field>
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <ReferenceList
            title="Make a 911 call"
            items={CALL_911_ITEMS}
            tone={{
              Icon: AlertCircleIcon,
              border: "border-red-200",
              head: "bg-red-50/70",
              iconClass: "text-red-500",
              titleClass: "text-red-700",
              numberClass: "text-red-500",
            }}
          />
          <ReferenceList
            title="Report to the doctor or nurse"
            items={REPORT_TO_CLINICIAN_ITEMS}
            tone={{
              Icon: NurseIcon,
              border: "border-slate-200",
              head: "bg-slate-50",
              iconClass: "text-slate-400",
              titleClass: "text-slate-700",
              numberClass: "text-slate-400",
            }}
          />
        </div>

        <Section icon={CheckCircleIcon} title="Kit for Emergency Preparedness">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <SubHeading>
                Supplies confirmed in the patient&apos;s home
              </SubHeading>
              <ul className="space-y-1.5">
                {PREPAREDNESS_KIT_ITEMS.map((item) => (
                  <li key={item.key}>
                    <label
                      className={`flex items-start gap-2 text-[11px] leading-snug ${
                        canEdit
                          ? "cursor-pointer text-slate-700"
                          : "text-slate-500"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className={checkboxClass}
                        checked={Boolean(values[item.key])}
                        disabled={!canEdit}
                        onChange={(e) => setValue(item.key, e.target.checked)}
                      />
                      <span>{item.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            <Field label="Extra Ventilation Items or Patient Requirements">
              <textarea
                className={`${textareaClass} min-h-[124px]`}
                value={values.extraSupplyNotes}
                disabled={!canEdit}
                placeholder="List medications, oxygen needs, or specific supplies…"
                onChange={(e) => setValue("extraSupplyNotes", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section
          icon={ShieldCheckIcon}
          title="Selection of Emergency Categories and Triage"
        >
          <div className="space-y-1.5">
            {TRIAGE_CATEGORIES.map((category) => {
              const selected = values.triageCategory === category.value;
              return (
                <label
                  key={category.value}
                  className={`flex items-start gap-2.5 rounded-md border px-3 py-2 transition-colors ${
                    selected
                      ? category.tone.selected
                      : "border-slate-200 hover:border-slate-300"
                  } ${canEdit ? "cursor-pointer" : "cursor-default"}`}
                >
                  <input
                    type="radio"
                    name="triageCategory"
                    className="mt-px h-3.5 w-3.5 shrink-0 border-slate-300 text-indigo-500 focus:ring-indigo-200 disabled:opacity-60"
                    checked={selected}
                    disabled={!canEdit}
                    onChange={() => setValue("triageCategory", category.value)}
                  />
                  <span className="min-w-0">
                    <span
                      className={`block text-[10px] font-bold uppercase tracking-wide ${category.tone.title}`}
                    >
                      {category.title}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                      {category.description}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          <div className="mt-2.5">
            <Field label="Triage Notes">
              <textarea
                className={`${textareaClass} min-h-[52px]`}
                value={values.triageNotes}
                disabled={!canEdit}
                placeholder="Additional context for the selected category…"
                onChange={(e) => setValue("triageNotes", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* Signature sections close out every form, above the office review. */}
        <FormSignatureSections
          {...signatureProps}
          patientSignatureUrl={
            showPatientSignature ? signatureProps.patientSignatureUrl : null
          }
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {/* On paper the comments are always static text, never a textarea. */}
        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      <FormActionBar
        permissions={permissions}
        status={form.status}
        canEdit={canEdit}
        saving={saving}
        onSubmit={submit}
        printMode={printMode}
      />
    </div>
  );
};

EmergencyCategoryForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  /** Renders the sheet frozen and without chrome, for the print page. */
  printMode: PropTypes.bool,
  /** Fired once the form's own data has loaded. */
  onReady: PropTypes.func,
};

export default EmergencyCategoryForm;
